import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableContainer,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  TableHead,
  Box,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import MANAGE_COLUMNS from '../../config/module-configs/assign-tags.config';
import Channels from '../form-elements/channels.component';
import {
  assignAdProviderTagsToChannel,
  getAdProviderTagsByChannel,
} from '../../services/ad-provider-tags.service';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import useAccess from '../../hooks/access.hook';
import RestrictedContent from '../form-elements/restricted-content.component';

const OzyPlaySettings = ({ channelId, fromChannel, viewOnly }) => {
  const FILTERS_KEY = 'assign-tags-channel';
  const columns = MANAGE_COLUMNS;

  const { hasOperationAccess } = useAccess();

  const [loading, setLoading] = useState(true);
  const [selectedChannelId, setSelectedChannelId] = useState(channelId);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const selectedChannelDetail = useRef(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({});

  useEffect(() => {
    setLoading(true);
    if (!fromChannel) {
      const localFilters = getLocalFilterValue(FILTERS_KEY);
      if (localFilters && Object.keys(localFilters).length > 0) {
        setSelectedChannelId(localFilters?.id || null);
        selectedChannelDetail.current = localFilters;
      }
    }

    if (selectedChannelId) {
      getAdProviderTagsByChannel(selectedChannelId)
        .then((res) => {
          Object.keys(res.data).forEach((key) => {
            setValue(`${key}.whaleTvSdkUrl`, res.data[key].whaleTvSdkUrl);
            setValue(`${key}.whaleTvSdkCpId`, res.data[key].whaleTvSdkCpId);
            setValue(
              `${key}.whaleTvSdkSourceId`,
              res.data[key].whaleTvSdkSourceId
            );
            setValue(
              `${key}.whaleTvPreRollAds`,
              res.data[key].whaleTvPreRollAds
            );
            setValue(
              `${key}.whaleTvMidRollAdsCount`,
              res.data[key].whaleTvMidRollAdsCount
            );
            setValue(
              `${key}.whaleTvAppLaunchAdsFrequency`,
              res.data[key].whaleTvAppLaunchAdsFrequency
            );
            setValue(
              `${key}.whaleTvPostRollAds`,
              res.data[key].whaleTvPostRollAds
            );
          });
          setLoading(false);
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
          setLoading(false);
        });
    } else {
      columns.forEach((item) => {
        setValue(`${item.id}.whaleTvSdkUrl`, '');
        setValue(`${item.id}.whaleTvSdkCpId`, '');
        setValue(`${item.id}.whaleTvSdkSourceId`, '');
        setValue(`${item.id}.whaleTvPreRollAds`, '');
        setValue(`${item.id}.whaleTvMidRollAdsCount`, '');
        setValue(`${item.id}.whaleTvAppLaunchAdsFrequency`, '');
        setValue(`${item.id}.whaleTvPostRollAds`, '');
      });
      // Patch for set value
      setTimeout(() => {
        setLoading(false);
      }, 50);
    }
  }, [selectedChannelId]);

  const formateData = (obj) => {
    const result = {
      type: 'WHALE_TV',
      otherData: {},
    };

    Object.entries(obj).forEach(([key, value]) => {
      const { ...rest } = value;
      result.otherData[key] = {
        ...rest,
      };
    });

    return result;
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const payload = formateData(data);
    assignAdProviderTagsToChannel(selectedChannelId, payload)
      .then(() => {
        setFormSubmitted(false);
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Whale TV Settings updated successfully.',
        });
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setFormSubmitted(false);

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };

  return (
    <Box display="flex" flexDirection="column">
      {!fromChannel && !viewOnly && (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={4} md={4}>
            <Channels
              id="user_channels"
              name="user_channels"
              label="Select Channel"
              defaultValue={selectedChannelDetail.current}
              onChange={(v) => {
                setLocalFilterValue(FILTERS_KEY, v);
                setSelectedChannelId(v?.id || null);
                selectedChannelDetail.current = v;
              }}
              sx={{ width: '100%' }}
              error={errors?.channels?.message || ''}
            />
          </Grid>
          {hasOperationAccess('advertising', 'manage') && (
            <Grid
              item
              xs={12}
              sm={8}
              md={8}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                type="submit"
                form="add-edit-form"
                color="primary"
                variant="contained"
                disabled={loading || !selectedChannelId || formSubmitted}
              >
                Save
              </Button>
            </Grid>
          )}
        </Grid>
      )}

      <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="6%">
                  <Typography variant="subtitle2">Platform</Typography>
                </TableCell>
                <TableCell width="5%">
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    CP ID
                  </Typography>
                </TableCell>
                <TableCell width="6%">
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    Source ID
                  </Typography>
                </TableCell>
                <TableCell width="14%">
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    SDK URL
                  </Typography>
                </TableCell>
                <TableCell width="7%">
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    App Launch Ads Frequency
                  </Typography>
                </TableCell>
                <TableCell width="6%">
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    PreRoll Ads?
                  </Typography>
                </TableCell>
                <TableCell width="8%">
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    Midroll after Every
                  </Typography>
                </TableCell>
                <TableCell width="6%">
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    PostRoll Ads?
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell width="100%" colSpan={6}>
                    <Loading />
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                columns.map((c) => (
                  <TableRow key={`col-${c.id}`}>
                    <TableCell
                      width="6%"
                      variant="body"
                      sx={{ border: 0, verticalAlign: 'center' }}
                    >
                      {c.label}
                    </TableCell>

                    <TableCell
                      width="5%"
                      variant="body"
                      sx={{
                        border: 0,
                        verticalAlign: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Controller
                        name={`${c.dataKey}.whaleTvSdkCpId`}
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="CP ID"
                            type="text"
                            error={!!errors?.[c.dataKey]?.whaleTvSdkCpId}
                            helperText={
                              errors?.[c.dataKey]?.whaleTvSdkCpId?.message ||
                              null
                            }
                            fullWidth
                            variant="filled"
                            disabled={!selectedChannelId}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell
                      width="6%"
                      variant="body"
                      sx={{
                        border: 0,
                        verticalAlign: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Controller
                        name={`${c.dataKey}.whaleTvSdkSourceId`}
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Source ID"
                            type="text"
                            error={!!errors?.[c.dataKey]?.whaleTvSdkSourceId}
                            helperText={
                              errors?.[c.dataKey]?.whaleTvSdkSourceId
                                ?.message || null
                            }
                            fullWidth
                            variant="filled"
                            disabled={!selectedChannelId}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell
                      width="14%"
                      variant="body"
                      sx={{
                        border: 0,
                        verticalAlign: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Controller
                        name={`${c.dataKey}.whaleTvSdkUrl`}
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="SDK URL"
                            type="text"
                            error={!!errors?.[c.dataKey]?.whaleTvSdkUrl}
                            helperText={
                              errors?.[c.dataKey]?.whaleTvSdkUrl?.message ||
                              null
                            }
                            fullWidth
                            variant="filled"
                            disabled={!selectedChannelId}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell
                      width="7%"
                      variant="body"
                      sx={{
                        border: 0,
                        verticalAlign: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Controller
                        name={`${c.dataKey}.whaleTvAppLaunchAdsFrequency`}
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Ads Frequency"
                            type="number"
                            error={
                              !!errors?.[c.dataKey]
                                ?.whaleTvAppLaunchAdsFrequency
                            }
                            helperText={
                              errors?.[c.dataKey]?.whaleTvAppLaunchAdsFrequency
                                ?.message || null
                            }
                            fullWidth
                            variant="filled"
                            disabled={!selectedChannelId}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell
                      width="6%"
                      variant="body"
                      sx={{
                        border: 0,
                        verticalAlign: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Controller
                        name={`${c.dataKey}.whaleTvPreRollAds`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RestrictedContent
                            id={`${c.dataKey}.whaleTvPreRollAds`}
                            name={`${c.dataKey}.whaleTvPreRollAds`}
                            label="PreRoll Ads"
                            defaultValue={value}
                            onChange={onChange}
                            sx={{ width: '100%' }}
                            error={
                              !!errors?.[c.dataKey]?.whaleTvPreRollAds
                                ?.message || null
                            }
                            fromWhaleTV
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell
                      width="8%"
                      variant="body"
                      sx={{
                        border: 0,
                        verticalAlign: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Controller
                        name={`${c.dataKey}.whaleTvMidRollAdsCount`}
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Midroll after Every"
                            type="number"
                            error={
                              !!errors?.[c.dataKey]?.whaleTvMidRollAdsCount
                            }
                            helperText={
                              errors?.[c.dataKey]?.whaleTvMidRollAdsCount
                                ?.message || null
                            }
                            fullWidth
                            variant="filled"
                            disabled={!selectedChannelId}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell
                      width="6%"
                      variant="body"
                      sx={{
                        border: 0,
                        verticalAlign: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Controller
                        name={`${c.dataKey}.whaleTvPostRollAds`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RestrictedContent
                            id={`${c.dataKey}.whaleTvPostRollAds`}
                            name={`${c.dataKey}.whaleTvPostRollAds`}
                            label="PostRoll Ads"
                            defaultValue={value}
                            onChange={onChange}
                            sx={{ width: '100%' }}
                            error={
                              !!errors?.[c.dataKey]?.whaleTvPostRollAds
                                ?.message || null
                            }
                            fromWhaleTV
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </form>

      {fromChannel && !loading && !viewOnly && (
        <Grid
          item
          xs={12}
          sm={8}
          md={8}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Button
            type="submit"
            form="add-edit-form"
            color="primary"
            variant="contained"
            disabled={loading || !selectedChannelId || formSubmitted}
          >
            Save Monetization Type
          </Button>
        </Grid>
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

OzyPlaySettings.propTypes = {
  channelId: PropTypes.number,
  fromChannel: PropTypes.bool,
  viewOnly: PropTypes.bool,
};
OzyPlaySettings.defaultProps = {
  channelId: null,
  fromChannel: false,
  viewOnly: false,
};

export default OzyPlaySettings;
